import React from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, Copy, FilePlus, Import, MoreHorizontal, Pencil, Search, Trash2, X } from 'lucide-react';

import { LoadingSpinner, InlineLoadingState } from '../../../components/LoadingSpinner';
import type { Lead, ProjectOffering } from '../../../types';
import type { OfferingVorlage } from '../../../features/stammdaten/types';
import { DEFAULT_OFFERING_TITLE } from '../../../utils/offeringLabels';
import { resolveLastUsedOfferingId, sortOfferingsForDisplay } from '../../../utils/offeringSelection';
import { useCapabilities } from '../../../hooks/useCapabilities';

type ProjectListItem = Pick<Lead, 'id' | 'display_id' | 'lead_title'>;

function formatOfferingCreatedAt(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('de-DE');
}

function OfferingMetaLine({ offering }: { offering: ProjectOffering }) {
  const createdLabel = formatOfferingCreatedAt(offering.created_at);
  const metaLine = [offering.display_id, createdLabel].filter(Boolean).join(' • ');
  if (!metaLine) return null;
  return <p className="text-[11px] text-app-text-subtle mt-0.5 truncate tabular-nums">{metaLine}</p>;
}

function LastUsedPill() {
  return (
    <span className="shrink-0 self-center inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-100 text-blue-800 border border-blue-200/60">
      zuletzt verwendet
    </span>
  );
}

const OFFERING_MENU_WIDTH = 200;

/** Drei-Punkte-Menü je Vorgang (Duplizieren / Löschen). Portal, damit die Liste es nicht abschneidet. */
function OfferingRowMenu({
  disabled,
  canDelete,
  onDuplicate,
  onDelete,
}: {
  disabled: boolean;
  canDelete: boolean;
  onDuplicate: () => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);
  const [pos, setPos] = React.useState<{ top: number; left: number } | null>(null);

  React.useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const update = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const panelHeight = 2 * 40 + 8;
      const left = Math.min(Math.max(8, rect.right - OFFERING_MENU_WIDTH), window.innerWidth - OFFERING_MENU_WIDTH - 8);
      const top = window.innerHeight - rect.bottom < panelHeight + 16 ? rect.top - 4 - panelHeight : rect.bottom + 4;
      setPos({ top, left });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Nur das Menü schließen, nicht den Dialog dahinter.
      e.stopImmediatePropagation();
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [open]);

  const itemClass = (itemDisabled: boolean, danger = false) =>
    `flex w-full items-center gap-2 whitespace-nowrap rounded-md px-3 py-2 text-left text-sm ${
      itemDisabled
        ? 'cursor-not-allowed text-slate-400'
        : danger
          ? 'text-red-600 hover:bg-red-50'
          : 'text-app-text-muted hover:bg-app-hover'
    }`;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className="p-2 rounded-lg text-slate-400 hover:text-app-text-muted hover:bg-app-hover disabled:opacity-50"
        aria-label="Weitere Aktionen"
        aria-haspopup="menu"
        aria-expanded={open}
        title="Weitere Aktionen"
      >
        <MoreHorizontal className="w-4 h-4" />
      </button>
      {open && pos
        ? createPortal(
            <div
              ref={panelRef}
              role="menu"
              style={{ top: pos.top, left: pos.left, width: OFFERING_MENU_WIDTH }}
              className="fixed z-[250] rounded-lg border border-app-border bg-app-surface p-1 shadow-lg"
            >
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  onDuplicate();
                }}
                className={itemClass(false)}
              >
                <Copy className="h-4 w-4 text-app-text-subtle" />
                Duplizieren
              </button>
              <button
                type="button"
                role="menuitem"
                disabled={!canDelete}
                title={canDelete ? undefined : 'Der letzte Vorgang kann nicht gelöscht werden'}
                onClick={() => {
                  if (!canDelete) return;
                  setOpen(false);
                  onDelete();
                }}
                className={itemClass(!canDelete, true)}
              >
                <Trash2 className="h-4 w-4" />
                Löschen
              </button>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

type ProjectOfferingPickerRowProps = {
  offering: ProjectOffering;
  isLastUsed: boolean;
  isEditing: boolean;
  editingTitle: string;
  rowBusy: boolean;
  selectBusy: boolean;
  globalBusy: boolean;
  canDelete: boolean;
  onSelect: () => void;
  onEditTitleChange: (value: string) => void;
  onStartEdit: () => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
};

function ProjectOfferingPickerRow({
  offering,
  isLastUsed,
  isEditing,
  editingTitle,
  rowBusy,
  selectBusy,
  globalBusy,
  canDelete,
  onSelect,
  onEditTitleChange,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onDelete,
  onDuplicate,
}: ProjectOfferingPickerRowProps) {
  const actionsDisabled = globalBusy || Boolean(rowBusy);

  return (
    <li className="flex items-stretch bg-app-surface hover:bg-app-hover/80">
      <div className="flex-1 min-w-0 flex items-center gap-2 px-4 py-3">
        {isEditing ? (
          <input
            type="text"
            value={editingTitle}
            disabled={rowBusy}
            autoFocus
            onChange={(e) => onEditTitleChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onSaveEdit();
              if (e.key === 'Escape') onCancelEdit();
            }}
            className="flex-1 min-w-0 text-sm font-semibold text-app-text border border-app-border rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-pipedrive-blue/20"
            aria-label="Vorgangsname"
          />
        ) : (
          <button
            type="button"
            disabled={actionsDisabled}
            onClick={onSelect}
            className="flex-1 min-w-0 text-left disabled:opacity-50"
          >
            <div className="text-sm font-semibold text-app-text">{offering.title?.trim() || DEFAULT_OFFERING_TITLE}</div>
            <OfferingMetaLine offering={offering} />
          </button>
        )}
        {!isEditing && isLastUsed ? <LastUsedPill /> : null}
        {!isEditing && selectBusy ? (
          <LoadingSpinner className="shrink-0 text-app-text-subtle" />
        ) : null}
      </div>
      <div className="flex items-center gap-0.5 px-2 border-l border-app-border shrink-0">
        {isEditing ? (
          <>
            <button
              type="button"
              disabled={rowBusy}
              onClick={onSaveEdit}
              className="p-2 rounded-lg text-emerald-700 hover:bg-emerald-50 disabled:opacity-50 text-xs font-semibold min-w-[2.25rem]"
            >
              {rowBusy ? <LoadingSpinner className="mx-auto" /> : 'OK'}
            </button>
            <button
              type="button"
              disabled={rowBusy}
              onClick={onCancelEdit}
              className="p-2 rounded-lg text-app-text-subtle hover:bg-app-hover disabled:opacity-50"
              aria-label="Abbrechen"
            >
              <X className="w-4 h-4" />
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              disabled={actionsDisabled}
              onClick={onStartEdit}
              className="p-2 rounded-lg text-slate-400 hover:text-app-text-muted hover:bg-app-hover disabled:opacity-50"
              aria-label="Vorgang umbenennen"
            >
              {rowBusy ? <LoadingSpinner /> : <Pencil className="w-4 h-4" />}
            </button>
            <OfferingRowMenu
              disabled={actionsDisabled}
              canDelete={canDelete}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
            />
          </>
        )}
      </div>
    </li>
  );
}

type ChooseOfferingForDocumentModalProps = {
  open: boolean;
  documentTypeLabel: string;
  projectRouteId: string;
  projectOfferings: ProjectOffering[];
  onClose: () => void;
  /** Zurück zur vorherigen Stufe (z. B. Dokumenttyp wählen); sonst wie onClose. */
  onBack?: () => void;
  onChosen: (offeringId: string) => void | Promise<void>;
  /** Nach Umbenennen/Löschen (z. B. Lead neu laden). */
  onOfferingsChange?: () => void | Promise<void>;
};

export const ChooseOfferingForDocumentModal: React.FC<ChooseOfferingForDocumentModalProps> = ({
  open,
  documentTypeLabel,
  projectRouteId,
  projectOfferings,
  onClose,
  onBack,
  onChosen,
  onOfferingsChange,
}) => {
  const [busy, setBusy] = React.useState(false);
  const [editingOfferingId, setEditingOfferingId] = React.useState<string | null>(null);
  const [editingTitle, setEditingTitle] = React.useState('');
  const [rowBusyId, setRowBusyId] = React.useState<string | null>(null);
  /** Welche Aktion läuft: Angebots-ID, „blank“ oder Import-Quell-ID. */
  const [pendingAction, setPendingAction] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [importMode, setImportMode] = React.useState(false);
  const [vorlageImportMode, setVorlageImportMode] = React.useState(false);
  const [projectSearch, setProjectSearch] = React.useState('');
  const [vorlageSearch, setVorlageSearch] = React.useState('');
  const [projects, setProjects] = React.useState<ProjectListItem[]>([]);
  const [vorlagen, setVorlagen] = React.useState<OfferingVorlage[]>([]);
  const [projectsLoading, setProjectsLoading] = React.useState(false);
  const [vorlagenLoading, setVorlagenLoading] = React.useState(false);
  const [selectedProjectId, setSelectedProjectId] = React.useState<string | null>(null);
  const [remoteOfferings, setRemoteOfferings] = React.useState<ProjectOffering[]>([]);
  const [remoteLoading, setRemoteLoading] = React.useState(false);
  // LV-Vorlagen gehören zum Modul Vorlagen.
  const { can } = useCapabilities();
  const canViewVorlagen = can('web.vorlagen.view');

  const cancelEditOffering = React.useCallback(() => {
    setEditingOfferingId(null);
    setEditingTitle('');
  }, []);

  React.useEffect(() => {
    if (!open) {
      setImportMode(false);
      setVorlageImportMode(false);
      setProjectSearch('');
      setVorlageSearch('');
      setSelectedProjectId(null);
      setRemoteOfferings([]);
      setVorlagen([]);
      setError(null);
      setBusy(false);
      setPendingAction(null);
      setEditingOfferingId(null);
      setEditingTitle('');
      setRowBusyId(null);
    }
  }, [open]);

  const finishChosen = React.useCallback(
    async (offeringId: string, actionKey: string) => {
      setBusy(true);
      setPendingAction(actionKey);
      setError(null);
      try {
        await Promise.resolve(onChosen(offeringId));
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Fehler');
        setBusy(false);
        setPendingAction(null);
      }
    },
    [onChosen],
  );

  React.useEffect(() => {
    if (!open || !importMode) return;
    let cancelled = false;
    setProjectsLoading(true);
    void fetch('/api/leads')
      .then((res) => (res.ok ? res.json() : []))
      .then((list) => {
        if (cancelled) return;
        const rows = Array.isArray(list) ? (list as ProjectListItem[]) : [];
        setProjects(rows.filter((p) => String(p.id) !== String(projectRouteId)));
      })
      .catch(() => {
        if (!cancelled) setProjects([]);
      })
      .finally(() => {
        if (!cancelled) setProjectsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, importMode, projectRouteId]);

  React.useEffect(() => {
    if (!open || !vorlageImportMode || !canViewVorlagen) return;
    let cancelled = false;
    setVorlagenLoading(true);
    void fetch('/api/offering-vorlagen')
      .then((res) => (res.ok ? res.json() : []))
      .then((list) => {
        if (!cancelled) setVorlagen(Array.isArray(list) ? (list as OfferingVorlage[]) : []);
      })
      .catch(() => {
        if (!cancelled) setVorlagen([]);
      })
      .finally(() => {
        if (!cancelled) setVorlagenLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, vorlageImportMode, canViewVorlagen]);

  React.useEffect(() => {
    if (!selectedProjectId) {
      setRemoteOfferings([]);
      return;
    }
    let cancelled = false;
    setRemoteLoading(true);
    void fetch(`/api/projects/${encodeURIComponent(selectedProjectId)}/offerings`)
      .then((res) => (res.ok ? res.json() : []))
      .then((list) => {
        if (!cancelled) setRemoteOfferings(Array.isArray(list) ? (list as ProjectOffering[]) : []);
      })
      .catch(() => {
        if (!cancelled) setRemoteOfferings([]);
      })
      .finally(() => {
        if (!cancelled) setRemoteLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedProjectId]);

  const handleDismiss = React.useCallback(() => {
    if (busy) return;
    onClose();
  }, [busy, onClose]);

  React.useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (editingOfferingId) {
        cancelEditOffering();
        return;
      }
      handleDismiss();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, handleDismiss, editingOfferingId, cancelEditOffering]);

  const sortedProjectOfferings = React.useMemo(
    () => sortOfferingsForDisplay(projectOfferings),
    [projectOfferings],
  );

  const sortedRemoteOfferings = React.useMemo(
    () => sortOfferingsForDisplay(remoteOfferings),
    [remoteOfferings],
  );

  const lastUsedOfferingId = React.useMemo(
    () => resolveLastUsedOfferingId(projectOfferings),
    [projectOfferings],
  );

  const filteredProjects = React.useMemo(() => {
    const q = projectSearch.trim().toLowerCase();
    if (!q) return projects.slice(0, 40);
    return projects
      .filter(
        (p) =>
          String(p.lead_title ?? '').toLowerCase().includes(q) ||
          String(p.display_id ?? '').toLowerCase().includes(q),
      )
      .slice(0, 40);
  }, [projects, projectSearch]);

  const filteredVorlagen = React.useMemo(() => {
    const q = vorlageSearch.trim().toLowerCase();
    if (!q) return vorlagen.slice(0, 50);
    return vorlagen
      .filter((v) => String(v.name ?? '').toLowerCase().includes(q))
      .slice(0, 50);
  }, [vorlagen, vorlageSearch]);

  const createBlank = async () => {
    setBusy(true);
    setPendingAction('blank');
    setError(null);
    try {
      const res = await fetch(`/api/projects/${encodeURIComponent(projectRouteId)}/offerings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(j.error ?? 'Vorgang konnte nicht erstellt werden.');
      }
      const created = (await res.json()) as ProjectOffering;
      if (!created?.id) throw new Error('Vorgang konnte nicht erstellt werden.');
      await Promise.resolve(onChosen(created.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fehler');
      setBusy(false);
      setPendingAction(null);
    }
  };

  const startEditOffering = (o: ProjectOffering) => {
    setEditingOfferingId(o.id);
    setEditingTitle(o.title?.trim() || DEFAULT_OFFERING_TITLE);
    setError(null);
  };

  const saveOfferingTitle = async (offeringId: string) => {
    const title = editingTitle.trim();
    if (!title) {
      setError('Der Name darf nicht leer sein.');
      return;
    }
    setRowBusyId(offeringId);
    setError(null);
    try {
      const res = await fetch(`/api/offerings/${encodeURIComponent(offeringId)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title }),
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(j.error ?? 'Speichern fehlgeschlagen.');
      }
      cancelEditOffering();
      await onOfferingsChange?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fehler');
    } finally {
      setRowBusyId(null);
    }
  };

  const deleteOffering = async (o: ProjectOffering) => {
    if (projectOfferings.length <= 1) {
      setError('Der letzte Vorgang eines Projekts kann nicht gelöscht werden.');
      return;
    }
    const label = o.title?.trim() || DEFAULT_OFFERING_TITLE;
    if (!window.confirm(`„${label}“ wirklich löschen? Positionen und Entwurf werden mit entfernt.`)) return;
    setRowBusyId(o.id);
    setError(null);
    try {
      const res = await fetch(`/api/offerings/${encodeURIComponent(o.id)}`, { method: 'DELETE' });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(j.error ?? 'Löschen fehlgeschlagen.');
      }
      if (editingOfferingId === o.id) cancelEditOffering();
      await onOfferingsChange?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fehler');
    } finally {
      setRowBusyId(null);
    }
  };

  /** Kopie im selben Projekt (Positionen + Entwurf); bleibt in der Liste, nichts wird geöffnet. */
  const duplicateOffering = async (o: ProjectOffering) => {
    const label = o.title?.trim() || DEFAULT_OFFERING_TITLE;
    setRowBusyId(o.id);
    setError(null);
    try {
      const res = await fetch(
        `/api/projects/${encodeURIComponent(projectRouteId)}/offerings/import-from`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ source_offering_id: o.id, title: `${label} (Kopie)` }),
        },
      );
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(j.error ?? 'Duplizieren fehlgeschlagen.');
      }
      await onOfferingsChange?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fehler');
    } finally {
      setRowBusyId(null);
    }
  };

  const importFrom = async (sourceOfferingId: string) => {
    setBusy(true);
    setPendingAction(sourceOfferingId);
    setError(null);
    try {
      const res = await fetch(
        `/api/projects/${encodeURIComponent(projectRouteId)}/offerings/import-from`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ source_offering_id: sourceOfferingId }),
        },
      );
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(j.error ?? 'Import fehlgeschlagen.');
      }
      const created = (await res.json()) as ProjectOffering;
      if (!created?.id) throw new Error('Import fehlgeschlagen.');
      await Promise.resolve(onChosen(created.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fehler');
      setBusy(false);
      setPendingAction(null);
    }
  };

  const importFromVorlage = async (vorlageId: string) => {
    setBusy(true);
    setPendingAction(vorlageId);
    setError(null);
    try {
      const res = await fetch(
        `/api/projects/${encodeURIComponent(projectRouteId)}/offerings/import-from-vorlage`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ vorlage_id: vorlageId }),
        },
      );
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(j.error ?? 'Import fehlgeschlagen.');
      }
      const created = (await res.json()) as ProjectOffering;
      if (!created?.id) throw new Error('Import fehlgeschlagen.');
      await Promise.resolve(onChosen(created.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fehler');
      setBusy(false);
      setPendingAction(null);
    }
  };

  if (!open) return null;

  const canDeleteOffering = sortedProjectOfferings.length > 1;

  return (
    <div
      className="fixed inset-0 z-[235] flex items-center justify-center p-4 bg-black/40 backdrop-blur-[2px]"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleDismiss();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="choose-offering-title"
        aria-busy={busy}
        className={`relative bg-app-surface rounded-md w-full max-w-lg shadow-2xl border border-app-border overflow-hidden flex flex-col max-h-[min(92vh,880px)] ${
          importMode || vorlageImportMode ? 'min-h-[min(82vh,640px)]' : 'min-h-0'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-app-border flex items-center gap-3 bg-app-surface-muted/80 shrink-0">
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              if (vorlageImportMode) setVorlageImportMode(false);
              else if (importMode) setImportMode(false);
              else (onBack ?? onClose)();
            }}
            className="p-1.5 rounded-lg text-app-text-subtle hover:bg-slate-200 hover:text-app-text transition-colors disabled:opacity-50"
            aria-label={importMode || vorlageImportMode || onBack ? 'Zurück' : 'Schließen'}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h2 id="choose-offering-title" className="text-base font-bold text-app-text flex-1 min-w-0">
            {vorlageImportMode
              ? 'Vorlage importieren'
              : importMode
                ? 'Vorgang importieren'
                : `Vorgang für „${documentTypeLabel}“`}
          </h2>
          <button
            type="button"
            disabled={busy}
            onClick={handleDismiss}
            className="p-1.5 rounded-lg text-app-text-subtle hover:bg-slate-200 hover:text-app-text transition-colors disabled:opacity-50 shrink-0"
            aria-label="Schließen"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div
          className={`relative p-3 sm:p-4 flex flex-col flex-1 min-h-0 ${
            importMode || vorlageImportMode ? 'overflow-hidden' : 'overflow-y-auto'
          }`}
        >
          {busy ? (
            <div
              className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-app-surface/75 backdrop-blur-[1px]"
              aria-live="polite"
            >
              <InlineLoadingState label="Wird geladen …" />
            </div>
          ) : null}

          {error ? (
            <p className="shrink-0 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2 mb-3">
              {error}
            </p>
          ) : null}

          {!importMode && !vorlageImportMode ? (
            <>
              {sortedProjectOfferings.length > 0 ? (
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-app-text-subtle">
                    Vorgänge dieses Projekts
                  </p>
                  <ul className="divide-y divide-pipedrive-border rounded-lg border border-app-border overflow-hidden">
                    {sortedProjectOfferings.map((o) => (
                      <ProjectOfferingPickerRow
                        key={o.id}
                        offering={o}
                        isLastUsed={o.id === lastUsedOfferingId}
                        isEditing={editingOfferingId === o.id}
                        editingTitle={editingTitle}
                        rowBusy={rowBusyId === o.id}
                        selectBusy={pendingAction === o.id}
                        globalBusy={busy}
                        canDelete={canDeleteOffering}
                        onSelect={() => void finishChosen(o.id, o.id)}
                        onEditTitleChange={setEditingTitle}
                        onStartEdit={() => startEditOffering(o)}
                        onSaveEdit={() => void saveOfferingTitle(o.id)}
                        onCancelEdit={cancelEditOffering}
                        onDelete={() => void deleteOffering(o)}
                        onDuplicate={() => void duplicateOffering(o)}
                      />
                    ))}
                  </ul>
                </div>
              ) : null}

              <div className={`flex flex-col gap-2 ${sortedProjectOfferings.length > 0 ? 'mt-5' : ''}`}>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void createBlank()}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left hover:bg-app-hover disabled:opacity-50"
                >
                  <FilePlus className="w-5 h-5 text-pipedrive-blue shrink-0" />
                  <span className="text-sm font-semibold text-app-text">Neuer Blanko-Vorgang</span>
                  {pendingAction === 'blank' ? (
                    <LoadingSpinner className="ml-auto text-app-text-subtle" />
                  ) : null}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => setImportMode(true)}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left hover:bg-app-hover disabled:opacity-50"
                >
                  <Import className="w-5 h-5 text-pipedrive-blue shrink-0" />
                  <span className="text-sm font-semibold text-app-text">
                    Vorgang aus anderem Projekt importieren
                  </span>
                </button>
                {canViewVorlagen ? (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => setVorlageImportMode(true)}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left hover:bg-app-hover disabled:opacity-50"
                  >
                    <Import className="w-5 h-5 text-pipedrive-blue shrink-0" />
                    <span className="text-sm font-semibold text-app-text">
                      Vorgang aus Vorlagen importieren
                    </span>
                  </button>
                ) : null}
              </div>
            </>
          ) : vorlageImportMode ? (
            <div className="flex flex-col flex-1 min-h-0 overflow-hidden gap-3">
              <div className="relative shrink-0">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="search"
                  value={vorlageSearch}
                  onChange={(e) => setVorlageSearch(e.target.value)}
                  placeholder="Vorlage suchen …"
                  className="w-full pl-9 pr-3 py-2 text-sm border border-app-border rounded-lg focus:outline-none focus:ring-2 focus:ring-pipedrive-blue/20"
                />
              </div>
              {vorlagenLoading ? (
                <InlineLoadingState label="Vorlagen werden geladen …" className="flex-1 min-h-[8rem]" />
              ) : filteredVorlagen.length === 0 ? (
                <p className="flex flex-1 items-center text-sm text-app-text-subtle min-h-[8rem] px-1">
                  Keine Vorlagen gefunden.
                </p>
              ) : (
                <ul className="flex-1 min-h-0 overflow-y-auto divide-y divide-pipedrive-border rounded-lg border border-app-border">
                  {filteredVorlagen.map((v) => (
                    <li key={v.id}>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => void importFromVorlage(v.id)}
                        className="w-full flex items-center gap-3 text-left px-4 py-3 hover:bg-app-hover disabled:opacity-50"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold text-app-text">{v.name}</div>
                          <p className="text-[11px] text-app-text-subtle mt-0.5">
                            {v.position_count ?? 0} Positionen
                          </p>
                        </div>
                        {pendingAction === v.id ? (
                          <LoadingSpinner className="shrink-0 text-app-text-subtle" />
                        ) : null}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : (
            <div
              className={`flex-1 min-h-0 grid gap-3 ${
                selectedProjectId
                  ? 'grid-rows-[minmax(0,1fr)_minmax(11rem,1fr)]'
                  : 'grid-rows-[minmax(0,1fr)]'
              }`}
            >
              <div className="flex flex-col min-h-0 overflow-hidden gap-3">
                <div className="relative shrink-0">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="search"
                    value={projectSearch}
                    onChange={(e) => setProjectSearch(e.target.value)}
                    placeholder="Projekt suchen …"
                    className="w-full pl-9 pr-3 py-2 text-sm border border-app-border rounded-lg focus:outline-none focus:ring-2 focus:ring-pipedrive-blue/20"
                  />
                </div>

                <div className="flex flex-col flex-1 min-h-0">
                  {projectsLoading ? (
                    <InlineLoadingState label="Projekte werden geladen …" className="flex-1 min-h-[8rem]" />
                  ) : (
                    <ul className="flex-1 min-h-0 overflow-y-auto rounded-lg border border-app-border divide-y divide-pipedrive-border">
                      {filteredProjects.length === 0 ? (
                        <li className="px-3 py-3 text-xs text-app-text-subtle">Keine Projekte gefunden.</li>
                      ) : (
                        filteredProjects.map((p) => {
                          const active = selectedProjectId === String(p.id);
                          return (
                            <li key={String(p.id)}>
                              <button
                                type="button"
                                onClick={() => setSelectedProjectId(String(p.id))}
                                className={`w-full text-left px-3 py-2.5 text-sm ${
                                  active ? 'bg-blue-50 font-semibold text-app-text' : 'hover:bg-app-hover text-app-text'
                                }`}
                              >
                                <span className="block truncate">{p.lead_title || p.display_id || 'Projekt'}</span>
                                <span className="text-[11px] text-app-text-subtle">{p.display_id}</span>
                              </button>
                            </li>
                          );
                        })
                      )}
                    </ul>
                  )}
                </div>
              </div>

              {selectedProjectId ? (
                <div className="flex flex-col min-h-0 overflow-hidden border-t border-app-border pt-3">
                  <p className="shrink-0 text-xs font-semibold text-app-text-subtle mb-2">
                    Vorgang zum Importieren
                  </p>
                  {remoteLoading ? (
                    <InlineLoadingState label="Vorgänge werden geladen …" className="flex-1 min-h-[6rem]" />
                  ) : remoteOfferings.length === 0 ? (
                    <p className="flex flex-1 items-center text-sm text-app-text-subtle min-h-[6rem] px-1">
                      In diesem Projekt gibt es keine Vorgänge.
                    </p>
                  ) : (
                    <ul className="flex-1 min-h-0 overflow-y-auto divide-y divide-pipedrive-border rounded-lg border border-app-border">
                      {sortedRemoteOfferings.map((o) => (
                        <li key={o.id}>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => void importFrom(o.id)}
                            className="w-full flex items-center gap-3 text-left px-4 py-3 hover:bg-app-hover disabled:opacity-50"
                          >
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-semibold text-app-text">{o.title?.trim() || DEFAULT_OFFERING_TITLE}</div>
                              <OfferingMetaLine offering={o} />
                            </div>
                            {pendingAction === o.id ? (
                              <LoadingSpinner className="shrink-0 text-app-text-subtle" />
                            ) : null}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
